import React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { WitnessSignatureArea } from '../../../../components/WitnessSignatureArea';
import { IsSigningObject } from '../../../../types/Signatures/IsSigningObject';
import { SignatureArea } from '../../../../components/SignatureArea';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Button } from '../../../../components/buttons/Button';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/statement/finalSignatures')({
  component: FinalSignaturesPage,
});

function FinalSignaturesPage() {
  const navigate = useNavigate();
  const { mutate: mutateChartDoc } = useUpdateChartSection();
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [updated, setUpdated] = React.useState(false);
  const [witnessOneName, setWitnessOneName] = React.useState('');
  const [witnessOneEmail, setWitnessOneEmail] = React.useState('');
  const [witnessOneIsDocuSign, setWitnessOneIsDocuSign] = React.useState(false);
  const [witnessTwoName, setWitnessTwoName] = React.useState('');
  const [witnessTwoEmail, setWitnessTwoEmail] = React.useState('');
  const [witnessTwoIsDocuSign, setWitnessTwoIsDocuSign] = React.useState(false);
  const [okToSubmit, setOkToSubmit] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);

  // const testCloudFunction = async () => {
  //   const response = await fetch(`${import.meta.env.VITE_CLOUDFUNCTIONS_URI}/testSentry`);
  //   if (response.status !== 200) {
  //     const res = await response.json();
  //     alert(`ERROR: ${res.message}`);
  //   }

  //   return response;
  // };

  // SET WITNESS NAMES LOCALLY
  React.useEffect(() => {
    if (chart?.WITNESS_SIGNATURE?.witnessName?.length > 0) {
      setWitnessOneName(chart.WITNESS_SIGNATURE.witnessName);
    }
    if (chart?.WITNESS_SIGNATURE?.witness2Name?.length > 0) {
      setWitnessTwoName(chart.WITNESS_SIGNATURE.witness2Name);
    }
  }, [chart]);

  // SET WITNESS EMAILS LOCALLY
  React.useEffect(() => {
    if (chart?.WITNESS_SIGNATURE?.witnessEmail?.length > 0) {
      setWitnessOneEmail(chart.WITNESS_SIGNATURE.witnessEmail);
    }
    if (chart?.WITNESS_SIGNATURE?.witness2Email?.length > 0) {
      setWitnessTwoEmail(chart.WITNESS_SIGNATURE.witness2Email);
    }
  }, [chart]);

  // WATCH FOR ABILITY TO SUBMIT
  React.useEffect(() => {
    if (
      // PATIENT HAS NOT YET SIGNED
      !chart?.PATIENT_SIGNATURE.signature1DataURI ||
      (chart?.PATIENT_SIGNATURE?.signature1DataURI && chart.PATIENT_SIGNATURE.signature1DataURI?.length === 0)
    ) {
      setOkToSubmit(false);
    }

    // WITNESS IS UNNAMED
    else if (witnessOneName?.length === 0 || witnessTwoName?.length === 0) {
      setOkToSubmit(false);
    }

    // LOCAL WITNESS ONE HAS NOT SIGNED
    else if (!witnessOneIsDocuSign && witnessOneName.length === 0) {
      setOkToSubmit(false);
    }

    // LOCAL WITNESS TWO HAS NOT SIGNED
    else if (!witnessTwoIsDocuSign && witnessTwoName.length === 0) {
      setOkToSubmit(false);
    }

    // DOCUSIGN WITNESS ONE HAS NOT SIGNED
    else if (
      (witnessOneIsDocuSign && witnessOneEmail.length === 0) ||
      (witnessOneIsDocuSign && chart.WITNESS_SIGNATURE.signature1Docusign === undefined)
    ) {
      setOkToSubmit(false);
    }

    // DOCUSIGN WITNESS TWO HAS NOT SIGNED
    else if (
      (witnessTwoIsDocuSign && witnessTwoEmail.length === 0) ||
      (witnessTwoIsDocuSign && chart.WITNESS_SIGNATURE?.signature2Docusign === undefined)
    ) {
      setOkToSubmit(false);
    }

    // PATIENT AND BOTH WITNESSES HAVE SIGNED
    else {
      setOkToSubmit(true);
    }
  }, [
    witnessOneName,
    witnessTwoName,
    witnessOneIsDocuSign,
    witnessTwoIsDocuSign,
    chart,
    witnessOneEmail?.length,
    witnessTwoEmail?.length,
  ]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'finalSignatures', patientInfo, value: true });
    }
  }, [patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    mutateChartDoc(
      {
        patientId: patientId,
        docData: {
          WITNESS_SIGNATURE: {
            witnessName: witnessForm.state.values.witnessName,
            witnessEmail: chart?.WITNESS_SIGNATURE?.witnessEmail ? chart.WITNESS_SIGNATURE.witnessEmail : '',
            signature1DataURI: chart?.WITNESS_SIGNATURE?.signature1DataURI
              ? chart.WITNESS_SIGNATURE.signature1DataURI
              : '',
            witness2Name: witnessForm.state.values.witness2Name,
            witness2Email: chart?.WITNESS_SIGNATURE?.witness2Email ? chart.WITNESS_SIGNATURE.witness2Email : '',
            signature2DataURI: chart?.WITNESS_SIGNATURE?.signature2DataURI
              ? chart.WITNESS_SIGNATURE.signature2DataURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setUpdated(true);
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FORM
  const witnessForm = useForm({
    defaultValues: {
      witnessName: chart?.WITNESS_SIGNATURE?.witnessName ? chart.WITNESS_SIGNATURE.witnessName : '',
      witness2Name: chart?.WITNESS_SIGNATURE?.witness2Name ? chart.WITNESS_SIGNATURE.witness2Name : '',
    },
    onSubmit: async () => {
      await handleIdInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="final-signatures-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="final-signatures-page-header" textContent="Final Signatures Page" />
          {/* <button
            onClick={() => {
              // throw new Error('Testing release based error boundaries');
              testCloudFunction();
            }}
          >
            Break the world
          </button> */}
          <div id="final-signatures-page-content" className="flex w-5/6 flex-row justify-around">
            <div id="script-container" className="w-full p-4">
              <h2 id="patient-final-sig-header" className="text-2xl font-bold">
                Patient
              </h2>
              <div id="patient-final-sig-container" className="">
                {chart.PATIENT_SIGNATURE && (
                  <SignatureArea
                    elementId={'patient-final-sig-'}
                    type={FileTypes.patientSig}
                    data={chart}
                    patientId={patientId}
                    isSigning={isSigning}
                    setIsSigning={setIsSigning}
                    cloudStoragePath={`/user/${patientId}/signatures/PATIENT_SIGNATURE_PatientFinalSignature`}
                    docToUpdate={'PATIENT_SIGNATURE'}
                    sigName={'patientFinal'}
                    canComeFromClone={true}
                  />
                )}
              </div>
              <div id="witness-1-sig-container" className="mt-12">
                <p className="text-2xl font-bold">
                  NOTE: Your Healthcare Agent cannot be either of the two required witnesses below.
                </p>
                <h2 className="mt-12 mr-2 justify-center text-2xl font-bold">{`Primary Witness`}</h2>
                <witnessForm.Field
                  name="witnessName"
                  children={(field) => (
                    <>
                      <FormInput
                        label="Witness 1 Name"
                        id="witness-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => {
                          setUpdated(false);
                          field.handleChange(e.target.value);
                          setWitnessOneName(e.target.value);
                        }}
                        required={false}
                      />
                    </>
                  )}
                />
                {chart.WITNESS_SIGNATURE && (
                  <WitnessSignatureArea
                    witnessName={witnessOneName}
                    witnessOneEmail={witnessOneEmail}
                    setWitnessOneEmail={setWitnessOneEmail}
                    setWitnessOneIsDocuSign={setWitnessOneIsDocuSign}
                    patientId={patientId}
                    witnessNumber={1}
                    data={chart}
                    patientInfo={patientInfo}
                    isSigning={isSigning}
                    setIsSigning={setIsSigning}
                    elementId={'witness-1-sig-'}
                  />
                )}
              </div>
              <div id="witness-2-sig-container" className="mt-12">
                <h2 className="mr-2 justify-center text-2xl font-bold">{`Second Witness`}</h2>
                <witnessForm.Field
                  name="witness2Name"
                  children={(field) => (
                    <>
                      <FormInput
                        label="Witness 2 Name"
                        id="witness-2-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => {
                          setUpdated(false);
                          field.handleChange(e.target.value);
                          setWitnessTwoName(e.target.value);
                        }}
                        required={false}
                      />
                    </>
                  )}
                />
                {chart.WITNESS_SIGNATURE && (
                  <WitnessSignatureArea
                    patientId={patientId}
                    witnessName={witnessTwoName}
                    witnessTwoEmail={witnessTwoEmail}
                    setWitnessTwoEmail={setWitnessTwoEmail}
                    setWitnessTwoIsDocuSign={setWitnessTwoIsDocuSign}
                    witnessNumber={2}
                    data={chart}
                    patientInfo={patientInfo}
                    isSigning={isSigning}
                    setIsSigning={setIsSigning}
                    elementId={'witness-2-sig-'}
                  />
                )}
              </div>
            </div>
          </div>
          {updated &&
          chart?.PATIENT_SIGNATURE?.signature1DataURI?.length > 0 &&
          (chart?.WITNESS_SIGNATURE?.witnessEmail?.length > 0 ||
            chart.WITNESS_SIGNATURE.signature1DataURI?.length > 0) ? (
            <Button
              id="review-card"
              type="button"
              textContent="Review Card"
              onClick={() => {
                navigate({ to: `/${patientId}/card/cardPreview` });
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center">
              {okToSubmit && (
                <Button
                  id="submit-witness-info"
                  type="submit"
                  textContent="Continue"
                  onClick={handleIdInfoUpdate}
                  disabled={false}
                />
              )}
              {updated && (
                <p className="text-lg text-red-500">
                  WARNING: You cannot complete your application without a witness signature.
                </p>
              )}
            </div>
          )}
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
